// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Dashboard\Productadd.js
// ==================== Imports ====================
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Container,
  Toast
} from 'react-bootstrap';
import axios from 'axios';
import CONFIG from '../../config';

// ==================== ProductAdd Component ====================
// Now supports edit mode via props: initialProduct, editMode
export default function ProductAdd({ show, handleClose, onProductAdded, initialProduct = null, editMode = false }) {
  // ---------------- State ----------------
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(''); // manual filename
  const [email, setEmail] = useState('');
  const [stock, setStock] = useState('');
  const [category, setCategory] = useState('');
  const [isUpdating, setIsUpdating] = useState(false); // for update (editMode)
  const [isAdding, setIsAdding] = useState(false); // for add (non-edit mode)
  const [isDeleting, setIsDeleting] = useState(false); // for delete only
  const [errorMsg, setErrorMsg] = useState('');
  // successMsg is intentionally not used for toast-based success UI to avoid duplicate popups
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Toast state: single toast for all success/failure actions
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastAction, setToastAction] = useState(''); // 'add' | 'update' | 'delete' | 'error'
  const [existingImage, setExistingImage] = useState(null);

  // ---------------- Helper: get logged-in user's email (best-effort) ----------------
  // Attempts several common locations (window globals, localStorage keys) to find an email.
  // Adjust keys to match your app if needed.
  function getLoggedInEmail() {
    try {
      // 1) Common global set by some apps
      if (window && window.__USER__ && window.__USER__.email) return String(window.__USER__.email);
      if (window && window.__auth && window.__auth.user && window.__auth.user.email) return String(window.__auth.user.email);
      // 2) localStorage 'user' or 'auth_user' storing JSON
      const tryKeys = ['user', 'auth_user', 'profile', 'auth', 'currentUser', 'current_user'];
      for (const key of tryKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.email) return String(parsed.email);
          if (parsed && parsed.user && parsed.user.email) return String(parsed.user.email);
        } catch (e) {
          // not JSON, maybe the value itself is an email
          if (typeof raw === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return raw;
        }
      }
      // 3) fallback: localStorage specific keys
      const direct = localStorage.getItem('userEmail') || localStorage.getItem('email');
      if (direct && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(direct)) return direct;
    } catch (err) {
      // ignore and return empty
    }
    return '';
  }

  // ---------------- Helper: compute public image URL ----------------
  const computeImageUrl = (productOrRel) => {
    const relOrProduct = productOrRel || '';
    if (typeof relOrProduct === 'object') {
      if (!relOrProduct) return null;
      if (relOrProduct.image_full_url) return relOrProduct.image_full_url || null;
      const rel = relOrProduct.image || relOrProduct.main_image || relOrRelOrFallback(productOrRel) || relOrProduct.image_url || '';
      return computeImageUrl(rel);
    }
    let rel = String(relOrProduct || '').trim();
    if (!rel) return null;
    if (/^https?:\/\//i.test(rel)) return rel;
    rel = rel.replace(/^\/?api\//i, '').replace(/^\/+/, '');
    const rawBase = (CONFIG?.API_URL || '').replace(/\/+$/, '') || '';
    const baseNormalized = rawBase.replace(/\/api$/i, '');
    if (!baseNormalized) return `/${rel}`;
    return `${baseNormalized}/${rel}`;
  };

  // fallback helper used in computeImageUrl above (safe)
  function relOrRelOrFallback(o) {
    return o && (o.main_image || o.image || o.image_url) || '';
  }

  // ---------------- Helper: compute API endpoints ----------------
  const computeApiBase = () => {
    const rawBase = (CONFIG?.API_URL || '').replace(/\/+$/, '') || '';
    if (!rawBase) return '';
    if (/\/api$/i.test(rawBase)) return rawBase;
    return `${rawBase}/api`;
  };

  // Populate form when opened in edit mode
  useEffect(() => {
    if (show && editMode && initialProduct) {
      setName(initialProduct.name || '');
      setPrice(initialProduct.price != null ? String(initialProduct.price) : '');
      setDescription(initialProduct.description || '');
      // email comes from logged-in user — but keep existing product owner email if you want to show it:
      const loggedEmail = getLoggedInEmail();
      setEmail(loggedEmail || initialProduct.email || initialProduct.owner_email || '');
      setStock(initialProduct.stock != null ? String(initialProduct.stock) : '');
      setCategory(initialProduct.category || '');
      const imgVal = initialProduct.image || initialProduct.main_image || initialProduct.image_url || initialProduct.image_full_url || '';
      setImage('');
      setSelectedFiles([]);
      setExistingImage(imgVal ? String(imgVal) : null);
      setErrorMsg('');
      setIsUpdating(false);
      setIsDeleting(false);
      setIsAdding(false);
    }

    if (!show && !editMode) {
      // when opening modal for add mode, auto-fill email from logged-in user
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialProduct, editMode]);

  // When modal is shown (both add and edit), auto-fill email from logged-in user (does not overwrite if already set by initialProduct in edit mode)
  useEffect(() => {
    if (show) {
      const logged = getLoggedInEmail();
      if (logged) setEmail(logged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // revoke object URLs to avoid memory leak
  useEffect(() => {
    return () => {
      selectedFiles.forEach(f => {
        if (f && f.preview) {
          URL.revokeObjectURL(f.preview);
        }
      });
    };
  }, [selectedFiles]);

  const resetForm = () => {
    setName('');
    setPrice('');
    setDescription('');
    setImage('');
    // reset email to logged-in user's email (don't leave it blank)
    const logged = getLoggedInEmail();
    setEmail(logged || '');
    setStock('');
    setCategory('');
    // revoke previews
    selectedFiles.forEach(f => { if (f && f.preview) URL.revokeObjectURL(f.preview); });
    setSelectedFiles([]);
    setExistingImage(null);
    setErrorMsg('');
    setIsUpdating(false);
    setIsDeleting(false);
    setIsAdding(false);
  };

  // ---------------- File Handler ----------------
  const handleAcceptedFiles = (files) => {
    if (files && files[0]) {
      const newFiles = [files[0]].map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          formattedSize: formatBytes(file.size),
        })
      );
      // revoke any previous previews
      selectedFiles.forEach(f => f && f.preview && URL.revokeObjectURL(f.preview));
      setSelectedFiles(newFiles);
      // clear manual filename if new file selected
      setImage('');
    }
  };

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // ---------------- Robust update to use PUT then fallback ----------------
  async function updateProductRobust(id, formData) {
    const apiBase = computeApiBase();
    if (!apiBase) throw new Error('API base not configured (CONFIG.API_URL)');

    const endpoint = `${apiBase}/products/${id}`;

    // Try native PUT first
    try {
      const putRes = await axios.put(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: () => true
      });
      if (putRes.status >= 200 && putRes.status < 300) return putRes;
    } catch (err) {
      console.warn('Native PUT failed:', err?.message || err);
    }

    // Fallback to POST + _method=PUT
    const fd = new FormData();
    for (const [k, v] of formData.entries()) fd.append(k, v);
    fd.append('_method', 'PUT');

    try {
      const postRes = await axios.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: () => true
      });
      return postRes;
    } catch (err) {
      console.error('Fallback POST (method spoof) failed:', err);
      throw err;
    }
  }

  // ---------------- Delete product ----------------
  async function deleteProductById(id) {
    const apiBase = computeApiBase();
    if (!apiBase) throw new Error('API base not configured (CONFIG.API_URL)');
    if (!id) throw new Error('product id required for delete');

    const endpoint = `${apiBase}/products/${id}`;
    try {
      const res = await axios.delete(endpoint, { validateStatus: () => true });
      return res;
    } catch (err) {
      throw err;
    }
  }

  // ---------------- Submit Handler ----------------
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isUpdating || isAdding) return; // guard against double submit
    setErrorMsg('');

    if (!name) {
      setErrorMsg('❌ Name is required!');
      return;
    }

    // NEW: description required
    if (!description || !description.trim()) {
      setErrorMsg('❌ Description is required!');
      return;
    }

    // NEW: category required
    if (!category) {
      setErrorMsg('❌ Category is required!');
      return;
    }

    if (email && !isValidEmail(email)) {
      setErrorMsg('❌ Please enter a valid email address!');
      return;
    }

    const hasNewFile = selectedFiles.length > 0;
    const hasManualFilename = image && image.trim().length > 0;
    const hasExisting = existingImage && String(existingImage).trim().length > 0;

    if (!editMode && !hasNewFile && !hasManualFilename) {
      setErrorMsg('❌ Please upload an image or enter image filename!');
      return;
    }
    if (editMode && !hasNewFile && !hasManualFilename && !hasExisting) {
      setErrorMsg('❌ Please upload an image or enter image filename (product has no existing image).');
      return;
    }

    // Prepare form data for create/update
    const formData = new FormData();
    formData.append('name', name);
    formData.append('price', price === '' ? 0 : Number(price));
    formData.append('description', description || 'No description provided');
    formData.append('email', email || '');
    formData.append('stock', stock ? Number(stock) : 0);
    formData.append('category', category || 'Other');

    if (hasNewFile) {
      formData.append('image', selectedFiles[0]);
      formData.append('main_image', selectedFiles[0]);
    } else if (hasManualFilename) {
      formData.append('image', image.trim());
      formData.append('main_image', image.trim());
    } else if (hasExisting) {
      formData.append('image', existingImage);
      formData.append('main_image', existingImage);
    }

    const apiBase = computeApiBase();
    if (!apiBase) {
      setErrorMsg('❌ API base not configured (CONFIG.API_URL).');
      return;
    }

    // EDIT MODE: Always perform create (POST) then delete old product (replace behavior).
    if (editMode && initialProduct) {
      setIsUpdating(true);
      const oldId = initialProduct.id ?? initialProduct._id ?? initialProduct.product_id;
      if (!oldId) {
        setErrorMsg('❌ Cannot replace: product id not found on initialProduct');
        setIsUpdating(false);
        return;
      }

      try {
        // 1) create new product (POST)
        const createEndpoint = `${apiBase}/products`;
        const postRes = await axios.post(createEndpoint, formData, { validateStatus: () => true });
        const okCreate = postRes && (postRes.status >= 200 && postRes.status < 300 || (postRes.data && (postRes.data.success === true || postRes.data.status === 'success')));
        if (!okCreate) {
          const msg = postRes?.data?.message || postRes?.data?.error || `Failed to create replacement (status ${postRes?.status})`;
          setErrorMsg('❌ ' + msg);
          setIsUpdating(false);
          return;
        }

        // normalize returned product
        let returnedProduct = null;
        if (postRes?.data) {
          if (postRes.data.product) returnedProduct = postRes.data.product;
          else returnedProduct = postRes.data;
        }
        if (returnedProduct && !returnedProduct.id && returnedProduct._id) returnedProduct.id = returnedProduct._id;
        if (returnedProduct) {
          const rel = returnedProduct.image || returnedProduct.main_image || returnedProduct.image_url || null;
          if (rel && !returnedProduct.image_full_url) returnedProduct.image_full_url = computeImageUrl(rel);
        }

        // 2) delete old product (best-effort). If delete fails, we still keep the new product and warn.
        let delOk = false;
        let delErrorMsg = null;
        try {
          const delRes = await deleteProductById(oldId);
          delOk = delRes && delRes.status >= 200 && delRes.status < 300;
          if (!delOk) {
            delErrorMsg = delRes?.data?.message || `Delete returned status ${delRes?.status}`;
          }
        } catch (delErr) {
          delOk = false;
          delErrorMsg = delErr?.message || String(delErr);
        }

        // Notify UI and listeners via toast
        setToastMessage('🔄 Product updated successfully');
        setToastAction('update');
        setToastVisible(true);

        // inform parent about new product
        if (onProductAdded && returnedProduct) {
          try { onProductAdded(returnedProduct); } catch (err) { console.warn(err); }
        }

        try {
          if (returnedProduct) window.dispatchEvent(new CustomEvent('product-added', { detail: returnedProduct }));
          if (delOk) window.dispatchEvent(new CustomEvent('product-deleted', { detail: { id: oldId } }));
        } catch (e) { /* ignore */ }

        // If delete failed, append a non-blocking warning in the error area (but overall success).
        if (!delOk) {
          console.warn('Warning: old product delete failed after successful create:', delErrorMsg);
          setErrorMsg((prev) => prev ? prev + '\nWarning: old product was not deleted: ' + delErrorMsg : ('Warning: old product was not deleted: ' + delErrorMsg));
        }

        // show toast for ~1s then reload page
        setIsUpdating(false);
        setTimeout(() => {
          setToastVisible(false);
          try { window.location.reload(); } catch (e) { console.warn('reload failed', e); }
        }, 1000);

        return;
      } catch (createErr) {
        setErrorMsg('❌ Create after delete failed: ' + (createErr?.message || 'unknown'));
        setIsUpdating(false);
        return;
      }
    }

    // NON-EDIT mode: create product normally
    if (!editMode) {
      setIsAdding(true);
      try {
        const endpoint = `${apiBase}/products`;
        const res = await axios.post(endpoint, formData, { validateStatus: () => true });
        const ok = res && (res.status >= 200 && res.status < 300 || (res.data && (res.data.success === true || res.data.status === 'success')));
        if (!ok) {
          const msg = res?.data?.message || res?.data?.error || `Failed to create (status ${res?.status})`;
          setErrorMsg('❌ ' + msg);
          setIsAdding(false);
          return;
        }

        let returnedProduct = null;
        if (res?.data) {
          if (res.data.product) returnedProduct = res.data.product;
          else returnedProduct = res.data;
        }
        if (returnedProduct && !returnedProduct.id && returnedProduct._id) returnedProduct.id = returnedProduct._id;
        if (returnedProduct) {
          const rel = returnedProduct.image || returnedProduct.main_image || returnedProduct.image_url || null;
          if (rel && !returnedProduct.image_full_url) returnedProduct.image_full_url = computeImageUrl(rel);
        }

        // show add success toast
        setToastMessage('✅ Product added successfully!');
        setToastAction('add');
        setToastVisible(true);

        if (onProductAdded && returnedProduct) {
          try { onProductAdded(returnedProduct); } catch (err) { console.warn(err); }
        }
        try {
          if (returnedProduct) window.dispatchEvent(new CustomEvent('product-added', { detail: returnedProduct }));
        } catch (e) { /* ignore */ }

        // After a short delay close modal & reset (keep behavior as requested)
        // reload the page after toast shows
        setIsAdding(false);
        setTimeout(() => {
          setToastVisible(false);
          try { window.location.reload(); } catch (e) { console.warn('reload failed', e); }
        }, 1000);
      } catch (err) {
        console.error('Error submitting product:', err);
        if (err.response?.data?.errors) {
          const errors = Object.values(err.response.data.errors).flat().join('\n');
          setErrorMsg(errors);
        } else if (err.response?.data?.message) {
          setErrorMsg(err.response?.data?.message);
        } else {
          setErrorMsg('❌ Something went wrong. Please try again.');
        }
        setIsAdding(false);
      }
    }
  };

  // ---------------- Delete Button Handler ----------------
  const handleDeleteClick = async () => {
    if (isDeleting) return;
    if (!initialProduct) return;
    const id = initialProduct.id ?? initialProduct._id ?? initialProduct.product_id;
    if (!id) {
      alert('Product id not found — cannot delete.');
      return;
    }
    const okConfirm = window.confirm('Are you sure you want to delete this product? This action cannot be undone.');
    if (!okConfirm) return;

    setIsDeleting(true);
    try {
      const delRes = await deleteProductById(id);
      const delOk = delRes && delRes.status >= 200 && delRes.status < 300;
      if (!delOk) {
        const msg = delRes?.data?.message || `Delete failed (status ${delRes?.status})`;
        setErrorMsg('❌ ' + msg);
        setIsDeleting(false);
        return;
      }

      // Notify parent & listeners via toast
      setToastMessage('❌ Product deleted successfully.');
      setToastAction('delete');
      setToastVisible(true);

      try { window.dispatchEvent(new CustomEvent('product-deleted', { detail: { id } })); } catch (e) { /* ignore */ }

      setIsDeleting(false);
      // after toast timeout reload page
      setTimeout(() => {
        setToastVisible(false);
        try { window.location.reload(); } catch (e) { console.warn('reload failed', e); }
      }, 1000);
    } catch (err) {
      setErrorMsg('❌ Delete failed: ' + (err?.message || 'unknown'));
      setIsDeleting(false);
    }
  };

  // ---------------- Email Validation ----------------
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ---------------- Remove selected file ----------------
  const removeFile = (index) => {
    // guard while actions in-flight
    if (isAdding || isUpdating || isDeleting) return;

    const newFiles = [...selectedFiles];
    const removed = newFiles.splice(index, 1);
    // revoke preview url(s)
    removed.forEach(f => f && f.preview && URL.revokeObjectURL(f.preview));
    setSelectedFiles(newFiles);

    // If removed file name matches manual filename (edge-case), clear manual filename
    if (removed[0] && removed[0].name && image && image.trim() === removed[0].name) {
      setImage('');
    }

    // If no selected files remain and no existing image, ensure placeholder shows (existingImage already controls)
    if (newFiles.length === 0 && !existingImage) {
      // nothing to do extra — state change already triggers UI placeholder
    }
  };

  // keyboard handler for accessible remove buttons
  const handleRemoveKey = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      removeFile(index);
    }
  };

  // ---------------- JSX ----------------
  return (
    <>
      {/* Toast Notification (top-right) */}
      <div style={styles.toastContainer}>
        <Toast
          show={toastVisible}
          onClose={() => setToastVisible(false)}
          delay={1000}
          autohide
          style={styles.toast}
        >
          <Toast.Header style={styles.toastHeader}>
            <strong className="me-auto">{toastAction === 'add' ? 'Added' : toastAction === 'update' ? 'Updated' : toastAction === 'delete' ? 'Deleted' : 'Message'}</strong>
            <small>Just now</small>
          </Toast.Header>
          <Toast.Body style={styles.toastBody}>
            {toastMessage || errorMsg}
          </Toast.Body>
        </Toast>
      </div>

      <Modal
        show={show}
        onHide={() => { handleClose(); resetForm(); }}
        centered
        fullscreen
        className="premium-product-modal"
      >
        <Modal.Header style={{ backgroundColor: '#405189', color: 'white', borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Modal.Title style={{ fontWeight: '600', fontSize: '1.25rem', color: 'white' }}>
              <i className={editMode ? "ri-edit-box-line me-2" : "ri-add-box-line me-2"}></i>
              {editMode ? 'Edit Product' : 'Add New Product'}
            </Modal.Title>

            <Button
              variant="outline-light"
              onClick={() => { handleClose(); resetForm(); }}
              style={{
                borderColor: 'white',
                color: 'white',
                padding: '4px 12px',
                fontSize: '0.9rem',
                borderRadius: '6px',
                backgroundColor: 'transparent',
              }}
            >
              <i className="ri-close-line me-1"></i>
              Close
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body style={styles.modalBody}>
          <Container fluid>
            {/* Alerts */}
            {errorMsg && (
              <Alert variant="danger" style={styles.alert}>
                <i className="ri-error-warning-line me-2"></i>
                {errorMsg}
              </Alert>
            )}

            <Row>
              {/* Left Column - Main Content */}
              <Col lg={8}>
                <Form onSubmit={handleSubmit}>
                  {/* Product Basic Info Card */}
                  <Card style={styles.card}>
                    <Card.Body style={styles.cardBody}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          <i className="ri-product-hunt-line me-2"></i>
                          Product Name *
                        </label>
                        <Form.Control
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter product title"
                          required
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          <i className="ri-file-text-line me-2"></i>
                          Product Description *
                        </label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Enter product description"
                          style={styles.textarea}
                          required
                        />
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Product Gallery Card */}
                  <Card style={styles.card}>
                    <Card.Header style={styles.cardHeader}>
                      <h5 style={styles.cardTitle}>Product Gallery</h5>
                    </Card.Header>
                    <Card.Body style={styles.cardBody}>
                      <div style={styles.imageUploadSection}>
                        <h6 style={styles.sectionSubtitle}>Product Image {editMode ? '(leave blank to keep current)' : '*'}</h6>
                        <p style={styles.helpText}>
                          {selectedFiles[0]
                            ? `Selected: ${selectedFiles[0].name}`
                            : image
                              ? `Using filename: ${image}`
                              : existingImage
                                ? `Using existing image: ${existingImage}`
                                : 'Upload an image or enter image filename'
                          }
                        </p>

                        <div style={styles.imageUploadArea}>
                          <div style={styles.imagePreviewContainer}>
                            <div style={styles.avatarContainer}>
                              <div style={styles.avatar}>
                                {selectedFiles[0] ? (
                                  <div style={styles.previewWrap}>
                                    <img
                                      src={selectedFiles[0].preview || undefined}
                                      alt="Preview"
                                      style={styles.previewImage}
                                    />
                                    {/* improved visible remove (✕) - accessible */}
                                    <button
                                      type="button"
                                      onClick={() => removeFile(0)}
                                      onKeyDown={(e) => handleRemoveKey(e, 0)}
                                      tabIndex={0}
                                      aria-label="Remove selected image"
                                      title="Remove selected image"
                                      style={styles.fileRemoveButton}
                                      disabled={isAdding || isUpdating || isDeleting}
                                    >
                                      <i className="ri-close-line" style={{ fontSize: '14px', color: '#fff' }}></i>
                                    </button>
                                  </div>
                                ) : image ? (
                                  <div style={styles.avatarPlaceholder}>
                                    <i className="ri-image-fill" style={styles.avatarIcon}></i>
                                    <small style={{ fontSize: '0.7rem', marginTop: '0.5rem' }}>
                                      Using: {image}
                                    </small>
                                  </div>
                                ) : existingImage ? (
                                  (() => {
                                    const src = computeImageUrl(existingImage);
                                    if (src) {
                                      return <img src={src || undefined} alt="Existing" style={styles.previewImage} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ''; }} />;
                                    }
                                    return (
                                      <div style={styles.avatarPlaceholder}>
                                        <i className="ri-image-fill" style={styles.avatarIcon}></i>
                                        <small style={{ fontSize: '0.7rem', marginTop: '0.5rem' }}>
                                          Existing: {existingImage}
                                        </small>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div style={styles.avatarPlaceholder}>
                                    <i className="ri-image-fill" style={styles.avatarIcon}></i>
                                    <small style={{ fontSize: '0.7rem', marginTop: '0.5rem' }}>
                                      No Image Selected
                                    </small>
                                  </div>
                                )}
                              </div>
                              <label htmlFor="product-image-input" style={styles.uploadLabel}>
                                <div style={styles.uploadButton}>
                                  <i className="ri-camera-fill" style={{ marginRight: '6px', fontSize: '1rem' }}></i>
                                  Upload
                                </div>
                              </label>
                              <input
                                className="d-none"
                                id="product-image-input"
                                type="file"
                                accept="image/png, image/gif, image/jpeg, image/jpg, image/webp"
                                onChange={(e) => handleAcceptedFiles(e.target.files)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* File Previews (for list view if more than one) */}
                        {selectedFiles.length > 0 && (
                          <div style={styles.filePreviews}>
                            <h6 style={styles.sectionSubtitle}>Selected Images</h6>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                              {selectedFiles.map((file, index) => (
                                <Card key={`${file.name}-${index}`} style={styles.filePreviewCard}>
                                  <div style={styles.fileRow}>

                                    {/* LEFT BOX */}
                                    <div style={styles.fileLeftBox}>
                                      <img src={file.preview} alt={file.name} style={styles.fileThumbnail} />

                                      <div style={{ display: "flex", flexDirection: "column" }}>
                                        <span style={{ fontWeight: 600 }}>{file.name}</span>
                                        <span style={{ fontSize: 12, color: "#6c757d" }}>{file.formattedSize}</span>
                                      </div>
                                    </div>

                                    {/* RIGHT BOX (REMOVE BTN) */}
                                    <div style={styles.fileRightBox}>
                                      <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        style={styles.removeBtn}
                                      >
                                        ✕
                                      </button>
                                    </div>

                                  </div>
                                </Card>
                              ))}

                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* General Info Card */}
                  <Card style={styles.card}>
                    <Card.Header style={styles.cardHeader}>
                      <h5 style={styles.cardTitle}>General Information</h5>
                    </Card.Header>
                    <Card.Body style={styles.cardBody}>
                      <Row>
                        <Col lg={6}>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>
                              <i className="ri-mail-line me-2"></i>
                              Your Email
                            </label>
                            <Form.Control
                              type="email"
                              value={email}
                              readOnly
                              placeholder="Logged-in user email"
                              style={{
                                ...styles.input,
                                backgroundColor: '#f1f3f5',
                                borderColor: email && !isValidEmail(email) ? '#dc3545' : '#e0e0e0',
                                cursor: 'not-allowed'
                              }}
                            />
                            {email && !isValidEmail(email) && (
                              <small style={styles.errorText}>
                                Warning: detected email looks invalid — please check your account.
                              </small>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Form>
              </Col>

              {/* Right Column - Sidebar */}
              <Col lg={4}>
                {/* Publish Card */}
                <Card style={styles.sidebarCard}>
                  <Card.Header style={styles.sidebarHeader}>
                    <i className="ri-send-plane-line me-2"></i>
                    Publish
                  </Card.Header>
                  <Card.Body>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Status</label>
                      <Form.Select style={styles.input}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="scheduled">Scheduled</option>
                      </Form.Select>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Visibility</label>
                      <Form.Select style={styles.input}>
                        <option value="public">Public</option>
                        <option value="hidden">Hidden</option>
                      </Form.Select>
                    </div>
                  </Card.Body>
                </Card>

                {/* Categories Card */}
                <Card style={styles.sidebarCard}>
                  <Card.Header style={styles.sidebarHeader}>
                    <i className="ri-price-tag-3-line me-2"></i>
                    Product Categories
                  </Card.Header>
                  <Card.Body>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Category *</label>
                      <Form.Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        style={styles.input}
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="Toys & Games">Toys & Games</option>
                        <option value="Sporting Goods">Sporting Goods</option>
                        <option value="Accessories">Accessories</option>
                        <option value="Clothes">Clothes</option>
                        <option value="Shoes">Shoes</option>
                        <option value="Books">Books</option>
                        <option value="Bags">Bags</option>
                      </Form.Select>
                    </div>
                  </Card.Body>
                </Card>

                {/* Product Summary Card */}
                <Card style={styles.sidebarCard}>
                  <Card.Header style={styles.sidebarHeader}>
                    <i className="ri-information-line me-2"></i>
                    Product Summary
                  </Card.Header>
                  <Card.Body>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryLabel}>Name:</span>
                      <span style={styles.summaryValue}>{name || 'Not set'}</span>
                    </div>

                    <div style={styles.summaryItem}>
                      <span style={styles.summaryLabel}>Category:</span>
                      <span style={styles.summaryValue}>{category || 'Not set'}</span>
                    </div>

                    <div style={styles.summaryItem}>
                      <span style={styles.summaryLabel}>Image:</span>
                      <span style={styles.summaryValue}>
                        {selectedFiles[0] ? 'Uploaded' : image ? 'Filename set' : existingImage ? 'Existing' : 'Not set'}
                      </span>
                    </div>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryLabel}>Email:</span>
                      <span style={{
                        ...styles.summaryValue,
                        color: email ? (isValidEmail(email) ? '#28a745' : '#dc3545') : '#6c757d'
                      }}>
                        {email ? (isValidEmail(email) ? 'Valid' : 'Invalid') : 'Not set'}
                      </span>
                    </div>
                    <div style={styles.summaryItem}>
                      <span style={styles.summaryLabel}>Status:</span>
                      <span style={{
                        ...styles.summaryValue,
                        color:
                          name &&
                          description &&
                          category &&
                          (!email || isValidEmail(email)) &&
                          (selectedFiles[0] || image || existingImage)
                            ? '#28a745'
                            : '#dc3545'
                      }}>
                        {
                          name &&
                          description &&
                          category &&
                          (!email || isValidEmail(email)) &&
                          (selectedFiles[0] || image || existingImage)
                            ? (editMode ? 'Ready to Update' : 'Ready to Add')
                            : 'Incomplete'
                        }
                      </span>
                    </div>
                  </Card.Body>
                </Card>

                {/* Actions Card */}
                <Card style={styles.sidebarCard}>
                  <Card.Header style={styles.sidebarHeader}>
                    <i className="ri-settings-3-line me-2"></i>
                    Actions
                  </Card.Header>
                  <Card.Body>
                    <Button
                      variant="success"
                      type="submit"
                      disabled={
                        (isAdding || isUpdating || isDeleting) ||
                        !name ||
                        !description ||
                        !category ||
                        (email && !isValidEmail(email)) ||
                        (!selectedFiles[0] && !image && !existingImage)
                      }
                      style={styles.submitButton}
                      className="w-100 mb-2"
                      onClick={handleSubmit}
                    >
                      {(isAdding || isUpdating) ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {editMode ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        <>
                          <i className="ri-add-circle-line me-2"></i>
                          {editMode ? 'Update' : 'Add Product'}
                        </>
                      )}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        handleClose();
                        if (!editMode) resetForm();
                      }}
                      style={styles.cancelButton}
                      className="w-100 mb-2"
                    >
                      <i className="ri-close-line me-2"></i>
                      Cancel
                    </Button>

                    {/* Delete button visible only in editMode */}
                    {editMode && (
                      <Button
                        variant="danger"
                        onClick={handleDeleteClick}
                        style={{ ...styles.cancelButton, backgroundColor: '#dc3545', borderColor: '#dc3545', marginTop: 6 }}
                        className="w-100"
                        disabled={isDeleting || isAdding || isUpdating}
                      >
                        {isDeleting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="ri-delete-bin-6-line me-2"></i>
                            Delete Product
                          </>
                        )}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </Modal.Body>

        {/* Embedded Styles */}
        <style>{`
          .premium-product-modal .modal-content {
            border: none;
            border-radius: 0;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .premium-product-modal .modal-header {
            border-bottom: 1px solid rgba(255,255,255,0.1);
          }
          .premium-product-modal .modal-body {
            flex: 1;
            overflow-y: auto;
          }
          .form-control:focus, .form-select:focus {
            border-color: #405189;
            box-shadow: 0 0 0 0.2rem rgba(64, 81, 137, 0.25);
          }
        `}</style>
      </Modal>
    </>
  );
}

// ==================== Styles ====================
const styles = {
  toastContainer: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
  },
  toast: {
    minWidth: '300px',
    border: 'none',
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  toastHeader: {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    borderRadius: '10px 10px 0 0',
  },
  toastBody: {
    backgroundColor: '#fff',
    borderRadius: '0 0 10px 10px',
    fontWeight: '500',
  },
  modalHeader: {
    backgroundColor: '#405189',
    color: 'white',
    borderBottom: 'none',
    padding: '1.5rem',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  closeButton: {
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    fontWeight: '500',
  },
  modalBody: {
    padding: '0',
    backgroundColor: '#f8f9fa',
  },
  alert: {
    borderRadius: '8px',
    margin: '1rem',
    border: 'none',
    fontWeight: '500',
  },
  card: {
    border: 'none',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem',
  },
  cardHeader: {
    backgroundColor: 'transparent',
    borderBottom: '1px solid #eaeaea',
    padding: '1.25rem 1.5rem',
  },
  cardTitle: {
    margin: '0',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#2c3e50',
  },
  cardBody: {
    padding: '1.5rem',
  },
  sidebarCard: {
    border: 'none',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem',
  },
  sidebarHeader: {
    backgroundColor: 'transparent',
    borderBottom: '1px solid #eaeaea',
    padding: '1rem 1.25rem',
    fontWeight: '600',
    color: '#2c3e50',
    display: 'flex',
    alignItems: 'center',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#34495e',
    fontSize: '0.9rem',
  },
input: {
  borderRadius: '6px',
  border: '1px solid #e0e0e0',
  padding: '0.75rem 1rem',
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
},

  textarea: {
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    resize: 'vertical',
    minHeight: '120px',
  },
  inputGroup: {
    display: 'flex',
  },
  inputGroupText: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRight: 'none',
    padding: '0.75rem 1rem',
    borderRadius: '6px 0 0 6px',
    color: '#6c757d',
    fontSize: '0.9rem',
  },
  imageUploadSection: {
    marginBottom: '1rem',
  },
  sectionSubtitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#2c3e50',
  },
  helpText: {
    fontSize: '0.8rem',
    color: '#6c757d',
    marginBottom: '1rem',
  },
  imageUploadArea: {
    marginBottom: '1.5rem',
  },
  imagePreviewContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  avatarContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  avatar: {
    width: '120px',
    height: '120px',
    borderRadius: '10px',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #dee2e6',
    overflow: 'hidden',
  },
  avatarPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: '#6c757d',
  },
  avatarIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  },
  previewWrap: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  uploadLabel: {
    cursor: 'pointer',
  },
  uploadButton: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '100px',
    height: '36px',
    backgroundColor: '#405189',
    color: 'white',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.9rem',
    fontWeight: '500',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  filePreviews: {
    marginTop: '1rem',
  },
  filePreviewCard: {
    border: '1px solid #eaeaea',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    padding: '0.75rem',
  },
  filePreviewContent: {
    width: '100%',
  },
  autoColumn: {
    flex: '0 0 auto',
    width: 'auto',
  },
  fileThumbnail: {
    width: 55,
    height: 55,
    borderRadius: 6,
    objectFit: "cover",
  },

  // small overlay remove button (on small thumbnails)
  fileRemoveButtonSmall: {
    position: 'absolute',
    top: -10,
    right: -10,
    width: 26,
    height: 26,
    borderRadius: '25%',
    backgroundColor: '#e74c3c',
    border: '2px solid rgba(255,255,255,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 3px 8px rgba(0,0,0,0.18)',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    outline: 'none',
  },

  fileRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "8px 4px",
  },

  fileLeftBox: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  fileRightBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "60px",
  },

  removeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "22px",
    fontWeight: "bold",
    color: "#e74c3c",
    cursor: "pointer",
    padding: "4px 8px",
    transition: "0.2s",
  },

  submitButton: {
    backgroundColor: '#405189',
    borderColor: '#405189',
    color: 'white',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontWeight: '600',
    fontSize: '1rem',
    border: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(64, 81, 137, 0.3)',
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    borderColor: '#6c757d',
    color: 'white',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontWeight: '600',
    fontSize: '1rem',
    border: 'none',
    transition: 'all 0.3s ease',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f1f1f1',
  },
  summaryLabel: {
    fontWeight: '500',
    color: '#6c757d',
    fontSize: '0.9rem',
  },
  summaryValue: {
    fontWeight: '600',
    color: '#2c3e50',
    fontSize: '0.9rem',
  },
  errorText: {
    color: '#dc3545',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
    display: 'block',
  },
  removeButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
};
